import { useCallback } from 'react'

import { XIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

import { cn } from '@/lib/cn'
import { DataTableFacetedFilter } from './data-table-faceted-filter'
import { useDataTableContext } from './data-table-root'
import { DataTableViewOptions } from './data-table-view-options'
import { UseDataTableReturn } from './use-data-table'

interface DataTableToolbarProps extends React.ComponentProps<'div'> {
  filters?: Array<Filter & { id: string }>
}

type Filter =
  | ({ type: 'text' } & FilterText)
  | ({ type: 'number' } & FilterNumber)
  | ({ type: 'date' } & FilterDate)
  | ({ type: 'select' } & FilterSelect)

type FilterText = {
  placeholder: string
  value: string
  onChange: (value: string) => void
}

type FilterNumber = {
  placeholder: string
  value: number
  onChange: (value: number) => void
}

type FilterDate = {
  placeholder: string
  value?: Date
  onChange: (value?: Date) => void
}

type FilterSelect = {
  placeholder: string
  options: Array<{ value: string; label: string }>
} & (
  | {
      multiple: true
      value: string[] | undefined
      onChange: (value: string[] | undefined) => void
    }
  | {
      multiple: false
      value: string | undefined
      onChange: (value?: string | undefined) => void
    }
)

export function DataTableToolbar({
  children,
  className,
  filters,
  ...props
}: DataTableToolbarProps) {
  const table = useDataTableContext()
  const hasActiveFilters = filters?.some((filter) => filter.value)

  return (
    <div
      role="toolbar"
      aria-orientation="horizontal"
      className={cn(
        'flex w-full items-start justify-between gap-2 p-1',
        className
      )}
      {...props}
    >
      <div className="flex flex-1 flex-wrap items-center gap-2">
        {filters?.map((filter) => (
          <DataTableToolbarFilter
            key={filter.id}
            filter={filter}
            table={table}
          />
        ))}
        {hasActiveFilters && (
          <Button
            aria-label="Reset filters"
            variant="outline"
            size="sm"
            className="border-dashed"
            onClick={table.resetFilters}
          >
            <XIcon />
            Reset
          </Button>
        )}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {children}
        <DataTableViewOptions />
      </div>
    </div>
  )
}

function DataTableToolbarFilter({
  filter,
  table,
}: {
  filter: Filter
  table: UseDataTableReturn
}) {
  const onFilterRender = useCallback(() => {
    if (!filter.type) return null

    switch (filter.type) {
      case 'text':
        return (
          <Input
            placeholder={filter.placeholder}
            value={filter.value ?? ''}
            onChange={(event) => filter.onChange(event.target.value)}
            className="w-56"
          />
        )

      case 'number':
        return (
          <div className="relative">
            <Input
              type="number"
              inputMode="numeric"
              placeholder={filter.placeholder}
              value={filter.value ?? ''}
              onChange={(event) =>
                filter.onChange(Number(event.target.value) || 0)
              }
              className={cn('h-8 w-[120px]')}
            />
          </div>
        )

      // case 'range':
      //   return <DataTableSliderFilter column={column} title={columnMeta.label ?? column.id} />

      // case 'date':
      // case 'dateRange':
      //   return (
      //     <DataTableDateFilter
      //       column={column}
      //       title={columnMeta.label ?? column.id}
      //       multiple={columnMeta.variant === 'dateRange'}
      //     />
      //   )

      case 'select':
        return <DataTableFacetedFilter {...filter} />

      // default:
      //   return null
    }
  }, [filter])

  return onFilterRender()
}
